import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Smartphone, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Repli d'encaissement : la passerelle est en panne, le membre paie par transfert
 *  Mobile Money sur le compte marchand et déclare la référence. L'administrateur
 *  valide depuis le Bastion (moins de 30 minutes). Aucune vente n'est perdue. */
export const PaiementTransfert = ({ offre, user, onFerme, onDeclare }) => {
  const [infos, setInfos] = useState(null);
  const [compte, setCompte] = useState(null);
  const [numeroPayeur, setNumeroPayeur] = useState(user?.contact || "");
  const [reference, setReference] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    axios.get(`${API}/paiements/direct/comptes`)
      .then((r) => { setInfos(r.data); setCompte(r.data.comptes?.[0] || null); })
      .catch(() => setInfos({ comptes: [] }));
  }, []);

  const declarer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const r = await axios.post(`${API}/paiements/direct/declarer`, {
        profile_id: user.id, kind: offre.kind, category: offre.category,
        period: offre.period, operateur: compte.operateur,
        numero_payeur: numeroPayeur.trim(), reference: reference.trim(),
      });
      toast.success("Transfert déclaré !", {
        description: `Votre abonnement de ${r.data.montant} FCFA est activé après vérification (moins de 30 minutes).`,
      });
      onDeclare?.(r.data);
      onFerme();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Déclaration impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      data-testid="paiement-transfert">
      <div className="w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        <header className="flex items-start justify-between gap-3 bg-[#2F363F] px-6 py-5 text-white">
          <div>
            <p className="font-heading text-xl font-extrabold">Payer par transfert</p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-white/70">
              <ShieldCheck size={12} /> Validation manuelle en moins de 30 minutes
            </p>
          </div>
          <button onClick={onFerme} data-testid="paiement-transfert-fermer"
            className="rounded-full p-2 transition-colors hover:bg-white/10"><X size={18} /></button>
        </header>

        {!infos ? (
          <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#E05638]" size={26} /></div>
        ) : !infos.comptes?.length ? (
          <p className="px-6 py-10 text-sm text-stone-600" data-testid="paiement-transfert-indispo">
            Le paiement par transfert n'est pas encore configuré. Notre équipe a été
            alertée et vous recontacte pour activer votre abonnement.
          </p>
        ) : (
          <form onSubmit={declarer} className="space-y-5 px-6 py-6">
            <div className="rounded-2xl bg-[#FAF8F5] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-500">
                1. Envoyez {offre.montant?.toLocaleString("fr-FR")} FCFA à
              </p>
              <div className="mt-3 space-y-2">
                {infos.comptes.map((c) => (
                  <button key={c.numero} type="button" onClick={() => setCompte(c)}
                    data-testid={`compte-${c.operateur.replace(/\s/g, "-").toLowerCase()}`}
                    className={`flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                      compte?.numero === c.numero
                        ? "border-[#E05638] bg-white" : "border-transparent bg-white/70"
                    }`}>
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-stone-800">
                      <Smartphone size={15} className="text-[#E05638]" /> {c.operateur}
                    </span>
                    <span className="font-mono text-sm font-bold text-stone-900">{c.numero}</span>
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-stone-500">Titulaire : <strong>{infos.titulaire}</strong></p>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">
                2. Votre numéro ayant envoyé l'argent
              </label>
              <input value={numeroPayeur} onChange={(e) => setNumeroPayeur(e.target.value)}
                required placeholder="07 00 00 00 00" data-testid="transfert-numero-payeur"
                className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 text-sm outline-none focus:border-[#E05638]" />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">
                3. Référence du transfert (reçue par SMS)
              </label>
              <input value={reference} onChange={(e) => setReference(e.target.value)}
                required minLength={4} placeholder="Ex. MP260707.1234.A56789" data-testid="transfert-reference"
                className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 text-sm outline-none focus:border-[#E05638]" />
            </div>

            <button type="submit" disabled={envoi || !compte} data-testid="transfert-declarer"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E05638] px-6 py-4 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-50">
              {envoi ? <Loader2 size={16} className="animate-spin" /> : null}
              J'ai envoyé l'argent, valider ma déclaration
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
